import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AutomationAutoStagedRollout, HuaweiAppGallerySettings, RunwayResponseType } from '../../types';
import * as React from 'react';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { selectAutomationById, selectIsReleaseRollback } from '../../selectors/selectors';
import { globalMessage } from '../../utils/globalMessage';
import EditHuaweiReleaseSettings from './../appSettings/EditHuaweiReleaseSettings';
import { ReleaseUpdatable } from '../../actions/updatable';
import { tryUpdateRelease, UpdateHuaweiAppStoreReleaseSettingsRequestForReleaseStep } from '../../actions/release';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { getRolloutDaysToPercentageFromAutomation } from '@/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface EditHuaweiReleaseSettingsContainerProps extends ReleaseComponentProps {
    open: boolean;
    releaseSettings?: HuaweiAppGallerySettings;
    onClose: () => void;
    onSuccess: () => void;
    autoStagedRolloutAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const EditHuaweiReleaseSettingsContainer: React.FC<EditHuaweiReleaseSettingsContainerProps> = (
    props: EditHuaweiReleaseSettingsContainerProps,
) => {
    const dispatch = useDispatch();

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const customStagedRolloutDaysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const customStagedRolloutEnabledAndActive = useSelector((state: RootState) => {
        const isEnabled = autoStagedRolloutAutomation?.enabled;

        if (props.releaseId) {
            const isRollback = selectIsReleaseRollback(state, props.releaseId);

            return isEnabled && !isRollback;
        }

        return isEnabled;
    });

    const [
        updateAppStoreSettingsStepRun,
        updateAppStoreSettingsIsLoadingStepRun,
        updateAppStoreSettingsResponseTypeStepRun,
        updateAppStoreSettingsErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateAppStoreSettings, props.stepRunComponentProps);

    const releaseSettingsUpdatedSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateHuaweiReleaseSettings);
    const releaseSettingsUpdateLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UpdateHuaweiReleaseSettings);
    const releaseSettingsUpdateErrorReleaseStep = useError(ReleaseUpdatable.UpdateHuaweiReleaseSettings);

    const releaseSettingsUpdatedSuccess =
        props.stepRunComponentProps ?
            updateAppStoreSettingsResponseTypeStepRun === RunwayResponseType.Success
        :   releaseSettingsUpdatedSuccessReleaseStep;
    const releaseSettingsUpdateLoading =
        props.stepRunComponentProps ? updateAppStoreSettingsIsLoadingStepRun : releaseSettingsUpdateLoadingReleaseStep;
    const releaseSettingsUpdateError =
        props.stepRunComponentProps ? updateAppStoreSettingsErrorStepRun : releaseSettingsUpdateErrorReleaseStep;

    const onSaveChanges = (stagedRelease: boolean, initialStagedRolloutPercentage?: number) => {
        const request: UpdateHuaweiAppStoreReleaseSettingsRequestForReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            stagedRelease,
            initialStagedRolloutPercentage,
        };

        if (props.stepRunComponentProps) {
            updateAppStoreSettingsStepRun({
                stagedRelease: stagedRelease,
                initialStagedRolloutPercentage: initialStagedRolloutPercentage,
            });
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateHuaweiReleaseSettings, request));
        }
    };

    React.useEffect(() => {
        if (releaseSettingsUpdatedSuccess) {
            globalMessage.success('AppGallery release defaults updated!');

            props.onSuccess();
        }
    }, [releaseSettingsUpdatedSuccess]);

    React.useEffect(() => {
        if (releaseSettingsUpdateError) {
            globalMessage.error(releaseSettingsUpdateError);
        }
    }, [releaseSettingsUpdateError]);

    return (
        <EditHuaweiReleaseSettings
            open={props.open}
            isConfiguringAppDefaults={false}
            isLoading={releaseSettingsUpdateLoading}
            customStagedRolloutEnabled={customStagedRolloutEnabledAndActive}
            customStagedRolloutDaysToPercent={customStagedRolloutDaysToPercent}
            huaweiAppGallerySettings={props.releaseSettings}
            onSubmit={onSaveChanges}
            onCancel={props.onClose}
        />
    );
};

export default EditHuaweiReleaseSettingsContainer;
